import React, { useEffect, useState } from 'react';

const Pricing = () => {
  const [plans, setPlans] = useState([]);

  const planMeta = {
    Free: {
      icon: 'graduation-cap',
      buttonText: 'Get Started',
      buttonClass: 'btn-outline-primary',
      popular: false,
    },
    Basic: {
      icon: 'book',
      buttonText: 'Choose Basic',
      buttonClass: 'btn-primary',
      popular: false,
    },
    Pro: {
      icon: 'star',
      buttonText: 'Choose Pro',
      buttonClass: 'btn-success',
      popular: true,
    },
    Premium: {
      icon: 'crown',
      buttonText: 'Choose Premium',
      buttonClass: 'btn-primary',
      popular: false,
    },
  };

  const comparisonFeatures = [
    'Public courses',
    'Private courses',
    'Student limit',
    'Support level',
    'Extra features',
  ];

  useEffect(() => {
    fetch('/api/v1/subscription_plans')
      .then((res) => res.json())
      .then((data) => {
        const transformedPlans = data.map((plan) => {
          const meta = planMeta[plan.name] || {};
          return {
            id: plan.id, // 🔥 Keep plan ID
            name: plan.name,
            icon: meta.icon || 'cube',
            price: `$${Number(plan.price)}`,
            period: '/month',
            privateCourses: plan.private_courses_limit,
            studentsLimit: plan.students_limit,
            allowPublic: plan.allow_public_courses,
            features: [
              plan.private_courses_limit > 0
                ? `${plan.private_courses_limit} private courses`
                : 'Create only a public course',
              plan.students_limit > 0
                ? `${plan.students_limit} students`
                : 'Unlimited students',
              plan.name === 'Free'
                ? 'Access community support'
                : plan.name === 'Basic'
                ? 'Email support'
                : plan.name === 'Pro'
                ? 'Priority email support'
                : 'Dedicated support',
              plan.name === 'Pro'
                ? 'Access to exclusive resources'
                : plan.name === 'Premium'
                ? 'Custom branding for courses'
                : null,
            ].filter(Boolean),
            buttonText: meta.buttonText || `Choose ${plan.name}`,
            buttonClass: meta.buttonClass || 'btn-primary',
            popular: meta.popular || false,
          };
        });
        setPlans(transformedPlans);
      })
      .catch((err) => console.error(err));
  }, []);

  const renderFeatureValue = (plan, feature) => {
    switch (feature) {
      case 'Public courses':
        return plan.allowPublic
          ? <i className="fas fa-check text-success"></i>
          : <i className="fas fa-times text-danger"></i>;
      case 'Private courses':
        return plan.privateCourses > 0
          ? plan.privateCourses
          : <i className="fas fa-times text-danger"></i>;
      case 'Student limit':
        return plan.studentsLimit > 0 ? plan.studentsLimit : 'Unlimited';
      case 'Support level':
        return plan.name === 'Free'
          ? 'Community'
          : plan.name === 'Basic'
          ? 'Email'
          : plan.name === 'Pro'
          ? 'Priority'
          : 'Dedicated';
      case 'Extra features':
        return plan.name === 'Pro'
          ? 'Exclusive resources'
          : plan.name === 'Premium'
          ? 'Custom branding'
          : <i className="fas fa-times text-danger"></i>;
      default:
        return '';
    }
  };

  return (
    <div className="pricing-page">
      {/* Pricing Plan Cards */}
      <section className="bg-white py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h1 className="display-4 fw-bold text-dark mb-3">Choose Your Plan</h1>
            <p className="lead">
              Flexible plans designed for students and instructors to unlock their full potential.
            </p>
          </div>
          <div className="row g-4">
            {plans.map((plan) => (
              <div key={plan.name} className="col-lg-3 col-md-6">
                <div
                  className={`card h-100 border-0 shadow-sm ${plan.popular ? 'border-success' : ''}`}
                >
                  {plan.popular && (
                    <div className="popular-badge">
                      <span className="badge bg-success px-3 py-2 rounded-pill">Most Popular</span>
                    </div>
                  )}
                  <div className="card-body p-4">
                    <div className="text-center mb-3">
                      <i className={`fas fa-${plan.icon} fa-2x text-${plan.icon === 'graduation-cap' ? 'secondary' : plan.icon === 'book' ? 'primary' : plan.icon === 'star' ? 'success' : 'primary'}`}></i>
                    </div>
                    <div className="text-center mb-3">
                      <h3 className="card-title h4 mb-2">{plan.name}</h3>
                      <div className="mb-3">
                        <span className="display-6 fw-bold text-dark">{plan.price}</span>
                        <span className="text-muted">{plan.period}</span>
                      </div>
                    </div>
                    <ul className="list-unstyled mb-4 text-start">
                      {plan.features.map((feature, idx) => (
                        <li className="mb-2" key={idx}>
                          <i className="fas fa-check text-success me-2"></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    {/* 🔥 Button now links to checkout with plan_id */}
                    <div className="text-center">
                      <a
                        href={`/users/subscription_checkout?plan_id=${plan.id}`}
                        className={`btn ${plan.buttonClass} w-100`}
                      >
                        {plan.buttonText}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compare Plans */}
      <section className="bg-light py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold text-dark mb-3">Compare Plans</h2>
            <p className="lead text-muted">See what's included in each plan</p>
          </div>
          <div className="table-responsive">
            <table className="table table-borderless bg-white rounded shadow-sm">
              <thead>
                <tr>
                  <th className="border-0 text-start fw-bold fs-5 p-4">Features</th>
                  {plans.map((plan) => (
                    <th
                      key={plan.name}
                      className={`border-0 text-center fw-bold fs-5 p-4 ${
                        plan.popular ? 'bg-light-green' : ''
                      }`}
                    >
                      {plan.name}
                      {plan.popular && <span className="badge bg-success ms-2">Popular</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((feature) => (
                  <tr key={feature}>
                    <td className="border-0 fw-semibold text-dark p-4">{feature}</td>
                    {plans.map((plan) => (
                      <td
                        key={`${plan.name}-${feature}`}
                        className={`border-0 text-center p-4 ${
                          plan.popular ? 'bg-light-green' : ''
                        }`}
                      >
                        {renderFeatureValue(plan, feature)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-primary py-5">
        <div className="container text-center">
          <h2 className="display-5 fw-bold text-white mb-3">Ready to start Teaching?</h2>
          <p className="lead text-white mb-4">Pick a plan today and grow with Qualify Academy</p>
          <a href="/pricing" className="btn btn-success btn-lg fw-bold px-5 py-3">
            Get Started Now
          </a>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
